import { throttle, isEqual } from 'lodash';
import Contacts from '../collections/Contacts';
import Contact from '../models/Contact';
import Conversations from '../collections/Conversations';
import { baseApi } from 'core/api';
import { useToastStore, useUserStore } from 'store/modules';

const state = () => ({
    _items: [],
    _pagination: null,
    _parameters: {
        page: 1,
        sortBy: ['disabled', 'created_at'],
        sortOrder: 'desc',
        sortType: null,
        fieldType: null,
        search: null,
        filtersListGroups: {},
        filtersList: [],
        opt_out: true,
        columns: [],
        number_id: null,
        length: 10,
    },
    _bulk_all: false,
    _editing: null,
    _deleting: null,
    _loading: false,
    _isContactsPage: null,
    _columnsKeys: [],
    _contactsFilters: {},
    _exportLoading: false,
    // [key: object (filters), value: Promise<object> (response)]
    _countMemorizeStore: [],
});

const getters = {
    contacts: (state) => state._items,
    contactsEditing: (state) => state._editing,
    contactsDeleting: (state) => state._deleting,
    contactsParameters: (state) => state._parameters,
    contactsPagination: (state) => state._pagination,
    contactsBulkAll: (state) => state._bulk_all,
    contactsLoading: (state) => state._loading,
    isContactsPage: (state) => state._isContactsPage,
    contactsFilters: (state) => state._contactsFilters,
    contactsExportLoading: (state) => state._exportLoading,
};

const utils = {
    handlePhoneType(form, contact) {
        const phone = form.number;
        let typePhone = '';

        if (!contact) return 'phone';
        if (contact.numbers) {
            for (let key in contact.numbers) {
                if (key === 'Phone' && contact.numbers[key] === phone) {
                    typePhone = 'phone';
                }

                if (key === 'Mobile Phone' && contact.numbers[key] === phone) {
                    typePhone = 'mobilephone';
                }
            }
        } else {
            if (contact.phone && contact.phone === phone) {
                typePhone = 'phone';
            }

            if (contact.mobile_phone && contact.mobile_phone === phone) {
                typePhone = 'mobilephone';
            }
        }

        return typePhone;
    },

    setContactsFilters(storeInstance) {
        return baseApi
            .get('core/contacts/filters', {
                params: {
                    withCustomFields: true,
                },
            })
            .then((response) => {
                storeInstance._setContactsFiltersLoad(response.data);
                return response.data;
            });
    },
};

// throttle actions
const throttleContactsFilters = throttle(utils.setContactsFilters, 3000, { trailing: false });

const actions = {
    setContactsPage(status) {
        this._setSetContactsPage(status);
    },

    contactLink({ form, id, contactIntegration }) {
        form.phone_type = utils.handlePhoneType(form, contactIntegration);

        return form.post(`core/contacts/${id}/link`).then((response) => {
            return new Contact(response.data);
        });
    },

    contactCheckLinked({ contact, selectedPhoneType }) {
        let data;

        if (!selectedPhoneType) {
            if (contact.mobile_phone) {
                data = {
                    mobile_phone:
                        contact.integration_type === 'salesforce'
                            ? contact.phones['Mobile Phone']
                            : contact.mobile_phone,
                };
            } else {
                data = {
                    phone:
                        contact.integration_type === 'salesforce'
                            ? contact.phones['Phone']
                            : contact.phone,
                };
            }
        } else if (selectedPhoneType === 'mobilephone') {
            data = {
                mobile_phone:
                    contact.integration_type === 'salesforce'
                        ? contact.phones['Mobile Phone']
                        : contact.mobile_phone,
            };
        } else if (selectedPhoneType === 'phone') {
            data = {
                phone:
                    contact.integration_type === 'salesforce'
                        ? contact.phones['Phone']
                        : contact.phone,
            };
        }

        if (contact.integration_type) {
            data.type = contact.integration_type;
        }

        return baseApi
            .get('core/integrations/contacts/linked', { params: data })
            .then((response) => {
                return response.data;
            });
    },

    contactsTag({ contactId, form }) {
        return form.post(`core/contacts/${contactId}/tags`).then((response) => {
            const contact = new Contact(response.data);

            this._setContactsTagged({
                contactIds: [contactId],
                tags: response.data.tags,
            });

            return contact;
        });
    },

    contactsNote({ contactId, form }) {
        return form.post(`core/contacts/${contactId}/notes`).then((response) => {
            const contact = new Contact(response.data);

            this._setContactsNotted({
                contactIds: [contactId],
                notes: response.data.notes,
            });

            return contact;
        });
    },

    contactsBulkTag(form) {
        return form.post('core/contacts/tags').then((response) => {
            this._setContactsTagged({
                contactIds: form.contact_ids,
                tags: response.data,
                bulk: true,
            });

            return response;
        });
    },

    contactsBulkCheck(form) {
        return form.post('core/contacts/bulk-check');
    },

    contactsRemoveBulkTag(form) {
        return form.deleteWithBody('core/contacts/tags/remove').then((response) => {
            this._setContactsTaggedRemove({
                contactIds: form.contact_ids,
                tags: response.data.tags,
                bulk: true,
            });

            return response;
        });
    },

    contactsLoad(contactId = {}, retry) {
        if (this._loading) return;
        if (retry === true) {
            this._setContactsRetryLoading();
        }

        this._setContactsLoading(true);

        return baseApi
            .get('core/contacts', {
                params: Object.assign(
                    {},
                    this._parameters,
                    { number_id: contactId },
                    { columns: this._columnsKeys }
                ),
            })
            .then((response) => {
                this._setContactsLoaded(response.data);
                return response;
            })
            .catch(() => {
                this._setContactsErrorLoading();
            })
            .finally(() => {
                this._setContactsLoading(false);
            });
    },

    contactsExport(contactColumns = {}, retry) {
        if (this._loading) return;
        if (retry === true) {
            this._setContactsRetryLoading();
        }
        this._setContactsExportLoading(true);

        return baseApi
            .get('core/contacts/export', {
                params: Object.assign({}, this._parameters, contactColumns), // Add columns for export
            })
            .then((response) => {
                this._setContactsExportLoading(false);
                return response;
            })
            .catch((e) => {
                this._setContactsErrorLoading();
                useToastStore().showToast({
                    type: 'danger',
                    message: 'Something went wrong. Please try again...',
                });
                return e;
            })
            .finally(() => {
                this._setContactsExportLoading(false);
            });
    },

    contactsLoadMore(retry) {
        if (this._loading) return;
        if (retry === true) {
            this._setContactsRetryLoading();
        }

        this._setContactsLoading(true);

        return baseApi
            .get('core/contacts', {
                params: this._parameters,
            })
            .then((response) => {
                this._setContactsLoadedMore(response.data);
                return response;
            })
            .catch(() => {
                this._setContactsErrorLoading();
            })
            .finally(() => {
                this._setContactsLoading(false);
            });
    },

    contactsClearSearch() {
        this._setContactsSearchCleared();
        this.contactsLoad();
    },

    createContact({ form, updateCollection = true, useAxios = false, data }) {
        if (useAxios) {
            return baseApi.post('core/contacts', data).then((response) => {
                return new Contact(response.data);
            });
        }

        return form.post('core/contacts').then((response) => {
            const contact = new Contact(response.data);

            if (updateCollection) {
                this._setContactCreated(contact);
                this.contactsLoad();
            }

            return contact;
        });
    },

    getConversationByContactId(contactId) {
        return baseApi.get(`core/contacts/${contactId}/conversations`).then((response) => {
            return new Conversations(response.data).models;
        });
    },

    contactsUpdate({ id, form, loadContacts = true, updateCollection = true }) {
        return form.put(`core/contacts/${id}`).then((response) => {
            const contact = new Contact(response.data);

            if (updateCollection) {
                this._setContactUpdated({ contact });
            }

            if (loadContacts) {
                this.contactsLoad();
            }

            return contact;
        });
    },

    updateContactsByIdFields({ id, props, key }) {
        return baseApi.patch(`core/contacts/${id}/fields`, props).then((response) => {
            const contact = new Contact(response.data);

            if (key === 'first_name' || key === 'last_name') {
                props.full_name = contact.full_name;
            }

            if (key === 'number') {
                props.formatted_number = contact.formatted_number;
                props.national_number = contact.national_number;
            }

            this._setContactUpdated({ contact, props });

            return contact;
        });
    },

    contactsSimpleUpdate(data) {
        return baseApi.put(`core/contacts/${data.id}`, data).then((response) => {
            return new Contact(response.data);
        });
    },

    contactsDelete({ id, form }) {
        return form.delete(`core/contacts/${id}`).then((response) => {
            this._setContactDeleted({
                id: id,
            });

            this.contactsLoad();
            return response;
        });
    },

    contactsBlock({ id, form }) {
        return form.post(`core/contacts/block/${id}`).then((response) => {
            const contact = new Contact(response.data);

            this._setContactUpdated({ contact });

            return response;
        });
    },

    contactsUnblock({ id }) {
        return baseApi.post(`core/contacts/unblock/${id}`).then((response) => {
            const contact = new Contact(response.data);

            this._setContactUpdated({ contact });

            return response;
        });
    },

    contactsBulkDelete(form) {
        return form.deleteWithBody('core/contacts').then((response) => {
            this._setContactsBulkDeleted();
            return response;
        });
    },

    contactCreated(contact) {
        if (!(contact instanceof Contact)) {
            contact = new Contact(contact);
        }

        this._setContactCreated(contact);
        this.contactsLoad();
    },

    contactUpdated(contact) {
        if (!(contact instanceof Contact)) {
            contact = new Contact(contact);
        }

        this._setContactUpdated({ contact });
    },

    contactDeleted({ id }) {
        this._setContactDeleted({ id });
        this.contactsLoad();
    },

    contactsDeleted(ids) {
        ids.forEach((id) => {
            this._setContactDeleted({ id });
        });
        this.contactsLoad();
    },

    contactSendOptInConfirmation(conversationId) {
        return baseApi.post(`core/contacts/opt-in/request/${conversationId}`).then((response) => {
            const contact = new Contact(response.data);

            this._setContactUpdated({ contact });
            return response;
        });
    },

    contactEditing(contact) {
        if (contact) {
            contact = new Contact(contact);
        }

        this._setContactEditing(contact);
    },

    contactDeleting(contact) {
        if (contact) {
            contact = new Contact(contact);
        }

        this._setContactDeleting(contact);
    },

    contactsUpdateSort(sort) {
        this._setContactsUpdateSort(sort);
        this.contactsLoad();
    },

    contactsUpdateColumns(columns) {
        this._setContactsUpdateColumns(columns);
        this.contactsLoad();
    },

    contactsUpdateSearch(search) {
        this._setContactsUpdateSearch(search);
        this.contactsLoad();
    },

    contactsUpdatePage(data) {
        if (typeof data === 'number') {
            this._setContactsUpdatePage(data);
            this.contactsLoad();
        } else {
            this._setContactsUpdatePage(data.page);
            this.contactsLoadMore();
        }
    },

    contactsUpdateLength(length) {
        this._setContactsUpdateLength(length);
        this.contactsLoad();
    },

    contactsCustomUpdateLengthSelector({ page, data }) {
        this._setContactsUpdateLength(page);
        this.contactsUpdatePage(data);
    },

    contactsUpdateParams(data) {
        this._setContactsUpdateParams(data);
    },

    contactsOptOutById({ id }) {
        return baseApi.put(`core/contacts/opt-out/${id}`).then((response) => {
            return new Contact(response.data);
        });
    },

    contactsOptInById({ id }) {
        return baseApi.put(`core/contacts/opt-in/${id}`).then((response) => {
            return new Contact(response.data);
        });
    },

    contactsOptInBulkStatus({ form, bulk }) {
        return form.put('core/contacts/opt-out').then((response) => {
            const contacts = new Contacts(response.data).models;

            contacts.forEach((contact) => {
                this._setContactUpdated({ contact });
            });

            return contacts;
        });
    },

    contactsPageOptInBulkStatus({ form, bulk }) {
        return form.put('core/contacts/bulk').then((response) => {
            const contacts = new Contacts(response.data).models;

            contacts.forEach((contact) => {
                this._setContactUpdated({ contact });
            });

            return contacts;
        });
    },

    loadContactsOptInStatus({ bulk }) {
        return baseApi
            .get('core/contacts', {
                params: {
                    contactIds: bulk,
                },
            })
            .then((response) => {
                return new Contacts(response.data.data).models;
            });
    },

    setContactsFilters() {
        return throttleContactsFilters(this);
    },

    async contactsFiltersApply(filters) {
        this._setContactsFiltersApply(filters);
        return this.contactsLoad();
    },

    setContactsBulkAll(bulkAllStatus) {
        this._setContactsBulkAll(bulkAllStatus);
    },

    contactsFilterCount(body) {
        let cancelToken = null;

        if (body.axiosCanceller) {
            cancelToken = body.axiosCanceller;
            delete body.axiosCanceller;
        }

        return baseApi
            .post('core/contacts/filters/count', body, {
                cancelToken,
            })
            .then((response) => {
                return response.data;
            });
    },

    contactFilterCountMemorize(data) {
        const { axiosCanceller, ...params } = data;

        const clearParams = Object.assign({}, params);

        const existCountMemorized = this._countMemorizeStore.find(([filter]) =>
            isEqual(clearParams, filter)
        );

        if (existCountMemorized) {
            return existCountMemorized[1];
        }

        const promise = baseApi
            .post('core/contacts/filters/count', clearParams)
            .then((response) => {
                return response.data;
            });

        this._setContactCountMemorized([clearParams, promise]);

        return promise;
    },

    contactsClearCountMemorize() {
        this._setContactClearMemorizedStore();
    },

    contactsSkippedCount(body) {
        return baseApi.post('core/contacts/filters/skipped/count', body).then((response) => {
            return response.data;
        });
    },

    estimateCredits(body) {
        return baseApi.post('core/contacts/filters/skipped/count', body).then((response) => {
            return response.data;
        });
    },

    contactsResetParameters() {
        this._setContactsResetParameters();
    },

    updateContactsFields(params) {
        return baseApi.post('core/contacts/fields', params).then((response) => {
            useUserStore()._setContactsFilterColumnUpdate(response.data);
            return response.data;
        });
    },

    getContactsById({ id }) {
        return baseApi.get(`core/contacts/${id}`).then((response) => {
            return new Contact(response.data);
        });
    },

    updateContactsLookupStatistics(params) {
        return baseApi.post('core/contacts/lookup/statistics', params).then((response) => {
            return response.data;
        });
    },

    // Private methods for all operations
    _setContactsFiltersLoad(data) {
        this._contactsFilters = data;
    },

    _setContactsLoading(status) {
        this._loading = status;
    },

    _setContactsUpdateParams(data) {
        this._parameters = {
            ...this._parameters,
            ...data,
        };
    },

    _setContactsResetParameters() {
        this._parameters = {
            page: 1,
            sortBy: ['disabled', 'created_at'],
            sortOrder: 'desc',
            sortType: null,
            fieldType: null,
            search: null,
            filtersList: [],
            length: 10,
        };
    },

    _setContactsBulkAll(bulkAllStatus) {
        this._bulk_all = bulkAllStatus;
    },

    _setContactsFiltersApply(filtersList) {
        this._parameters.filtersList = filtersList;
        this._parameters.page = 1;
    },

    _setContactCreated(data) {
        if (this._items === null) {
            return;
        }

        this._items.unshift(data);

        if (this._pagination) {
            ++this._pagination.total;
        }
    },

    _setContactUpdated({ contact, props }) {
        if (this._items === null) return;

        this._items = this._items.map((o) => {
            if (o.id === contact.id) {
                if (props) {
                    return contact.update(props);
                }

                return contact;
            } else {
                return o;
            }
        });
    },

    _setContactDeleted({ id }) {
        if (this._items === null) return;
        if (this._items === false) return;

        this._items = this._items.filter((o) => o.id !== id);

        if (this._pagination) {
            --this._pagination.total;
        }
    },

    _setContactsBulkDeleted() {
        if (!this._items || !this._items.length) {
            return;
        }

        this._items = [];
        this._pagination.total = 0;
    },

    _setSetContactsPage(status) {
        this._isContactsPage = status;
    },

    _setContactsTagged({ contactIds, tags, bulk = false }) {
        this._bulk_all = false;

        if (!this._items || !this._items.length) {
            return;
        }

        this._items = this._items.map((item) => {
            if (contactIds.includes(item.id)) {
                if (bulk) {
                    item.tags = item.tags.concat(
                        tags.filter((tag) => {
                            return !item.tags.find((t) => t.id === tag.id);
                        })
                    );
                } else {
                    item.tags = tags;
                }
            }

            return item;
        });
    },

    _setContactsTaggedRemove({ contactIds, tags, bulk = false }) {
        this._bulk_all = false;

        if (!this._items || !this._items.length) {
            return;
        }

        tags = tags ? tags.map((tag) => +tag) : [];

        this._items = this._items.map((item) => {
            if (contactIds.includes(item.id)) {
                item.tags = item.tags.filter((tag) => {
                    return !tags.includes(tag.id);
                });
            }

            return item;
        });
    },

    _setContactsLoaded(data) {
        const contacts = new Contacts(data.data);

        this._items = contacts.models;
        this._bulk_all = false;

        if (data.type === 'integrations') {
            const pages = this._pagination
                ? this._pagination.pages
                    ? this._pagination.pages
                    : [1]
                : [1];
            const existPage = pages.find((page) => page === data.next_page);

            if (!existPage) {
                pages.push(data.next_page);
            }

            const pageIndex = pages.findIndex((page) => page === data.current_page);
            const pageCurrent = pageIndex + 1;
            const from = pageCurrent === 1 ? pageCurrent : data.per_page * (pageCurrent - 1) + 1;
            const to = pageCurrent * data.per_page;

            this._pagination = {
                type: data.type,
                current_page: data.current_page,
                next_page: data.next_page,
                last_page: Math.ceil(data.total / data.per_page),
                per_page: data.per_page,
                to: to > data.total ? data.total : to,
                from: from,
                total: data.total,
                has_more: data.has_more,
                pages: pages,
                pageIndex: pageIndex,
            };
        } else {
            this._pagination = {
                type: '',
                current_page: data.current_page,
                from: data.from,
                last_page: data.last_page,
                per_page: data.per_page,
                to: data.to,
                total: data.total,
                next_page_url: data.next_page_url,
            };
        }
    },

    _setContactsLoadedMore(data) {
        const contacts = new Contacts(data.data);

        if (Array.isArray(this._items) && this._items.length) {
            this._items = this._items.concat(contacts.models);
        } else {
            this._items = contacts.models;
        }

        this._bulk_all = false;
        this._pagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            next_page_url: data.next_page_url,
        };
    },

    _setContactsRetryLoading() {
        this._items = null;

        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setContactsErrorLoading() {
        this._items = false;

        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setContactEditing(data) {
        this._editing = data;
    },

    _setContactDeleting(data) {
        this._deleting = data;
    },

    _setContactsUpdateSort(data) {
        this._parameters.sortBy = ['disabled', data.sortBy];
        this._parameters.sortOrder = data.sortOrder;
        this._parameters.sortType = data.sortType;
        this._parameters.fieldType = data.fieldType;
    },

    _setContactsUpdateColumns(data) {
        this._columnsKeys = data.columns;
        this._parameters.columns = data.columns;
    },

    _setContactsUpdateSearch(data) {
        this._parameters.search = data;
        this._parameters.page = 1;
    },

    _setContactsUpdatePage(data) {
        this._parameters.page = data;
    },

    _setContactsUpdateLength(data) {
        this._parameters.length = data;
    },

    _setContactsSearchCleared() {
        this._parameters.search = null;
    },

    _setContactsExportLoading(status) {
        this._exportLoading = status;
    },

    _setContactCountMemorized(data) {
        this._countMemorizeStore = [...this._countMemorizeStore, data];
    },

    _setContactClearMemorizedStore() {
        this._countMemorizeStore = [];
    },

    _setContactsNotted({ contactIds, notes }) {
        // This method was referenced in actions but not found in mutations
        // Adding it to maintain compatibility
        if (!this._items || !this._items.length) {
            return;
        }

        this._items = this._items.map((item) => {
            if (contactIds.includes(item.id)) {
                item.notes = notes;
            }
            return item;
        });
    }
};

export default {
    state,
    getters,
    actions
};
